import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countryCodes } from "../../Login/components/CountryCode";
import { MapPin, Mail, Phone, Clock, CheckCircle, AlertCircle, Edit3 } from "lucide-react"; // Icons for better UI

const VisaHistoryForm = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchApi();
  }, []);

  const fetchApi = async () => {
    setLoading(true);
    try {
      const response = await makeRequestAuth("get", "/user/getContact");
      if (response?.data) setPreview(response.data);
    } catch (err) {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (data) => {
    const newErrors = {};
    if (!data.indiaAddress) newErrors.indiaAddress = "India address is required";
    if (!data.canadaAddress) newErrors.canadaAddress = "Canada address is required";
    if (!data.email) newErrors.email = "Email is required";
    if (!data.mobile) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{6,15}$/.test(data.mobile)) {
      newErrors.mobile = "Mobile number must be 6-15 digits";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const payload = {
      indiaAddress: form.indiaAddress.value.trim(),
      canadaAddress: form.canadaAddress.value.trim(),
      email: form.email.value.trim(),
      mobile: `${form.countryCode.value}${form.mobile.value.trim()}`,
    };

    const validationErrors = validateForm({ ...payload, mobile: form.mobile.value.trim() });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/user/createOrUpdateContact", payload);
      setPreview(res?.data || { ...payload, status: "pending" });
      showSuccessToast(res?.message || "Contact details saved successfully");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-orange-100 text-orange-700 border-orange-200", icon: <Clock size={16} /> },
    approve: { color: "bg-green-100 text-green-700 border-green-200", icon: <CheckCircle size={16} /> },
    reject: { color: "bg-red-100 text-red-700 border-red-200", icon: <AlertCircle size={16} /> },
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-12 px-4">
      <ToastContainer />

      {/* Modern Loader */}
      {loading && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#132b45] rounded-full animate-spin"></div>
            <p className="mt-4 font-semibold text-[#132b45] animate-pulse">Updating information...</p>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="bg-[#132b45] p-6 text-white text-center">
            <h2 className="text-2xl font-bold">Contact Information</h2>
            <p className="text-gray-300 text-sm mt-1">Please provide your latest address and contact details</p>
          </div>

          <div className="p-8">
            {preview ? (
              /* --- REFINED PREVIEW MODE --- */
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <PreviewItem icon={<MapPin className="text-blue-500" />} label="India Address" value={preview.indiaAddress} />
                  <PreviewItem icon={<MapPin className="text-red-500" />} label="Canada Address" value={preview.canadaAddress} />
                  <PreviewItem icon={<Mail className="text-purple-500" />} label="Email Address" value={preview.email} />
                  <PreviewItem icon={<Phone className="text-green-500" />} label="Mobile Number" value={preview.mobile} />
                </div>

                <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-bold uppercase ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-gray-100"}`}>
                    {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon}
                    {preview.status || "Pending Review"}
                  </div>

                  {preview.status === "reject" && (
                    <button
                      onClick={() => setPreview(null)}
                      className="flex items-center gap-2 bg-[#132b45] hover:bg-black text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg active:scale-95"
                    >
                      <Edit3 size={18} /> Edit Application
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* --- REFINED FORM MODE --- */
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <TextareaField label="India Address" name="indiaAddress" placeholder="Enter permanent address in India" error={errors.indiaAddress} />
                  <TextareaField label="Canada Address" name="canadaAddress" placeholder="Enter current address in Canada" error={errors.canadaAddress} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField label="Email Address" name="email" type="email" placeholder="example@email.com" error={errors.email} />
                    <MobileField error={errors.mobile} />
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-[#132b45] text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                  >
                    Save & Proceed
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TextareaField = ({ label, name, error, placeholder }) => (
  <div className="flex flex-col">
    <label className="text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">{label}</label>
    <textarea
      name={name}
      rows="3"
      placeholder={placeholder}
      className={`w-full bg-gray-50 border-2 rounded-xl px-4 py-3 transition-all outline-none focus:bg-white focus:border-[#132b45] ${error ? "border-red-400" : "border-gray-100"
        }`}
    />
    {error && <span className="text-red-500 text-xs mt-1 font-medium ml-1 flex items-center gap-1">⚠ {error}</span>}
  </div>
);

const InputField = ({ label, name, type, error, placeholder }) => (
  <div className="flex flex-col">
    <label className="text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">{label}</label>
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      className={`w-full bg-gray-50 border-2 rounded-xl px-4 py-3 transition-all outline-none focus:bg-white focus:border-[#132b45] ${error ? "border-red-400" : "border-gray-100"
        }`}
    />
    {error && <span className="text-red-500 text-xs mt-1 font-medium ml-1 flex items-center gap-1">⚠ {error}</span>}
  </div>
);

const MobileField = ({ error }) => (
  <div className="flex flex-col">
    <label className="text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">Mobile Number</label>
    <div className="flex gap-2">
      <select
        name="countryCode"
        className="bg-gray-50 border-2 border-gray-100 rounded-xl px-2 py-3 focus:border-[#132b45] outline-none"
        defaultValue="+1"
      >
        {countryCodes.map((c) => (
          <option key={c.code} value={c.code}>{c.code}</option>
        ))}
      </select>
      <input
        name="mobile"
        type="tel"
        placeholder="9876543210"
        className={`flex-1 bg-gray-50 border-2 rounded-xl px-4 py-3 outline-none focus:bg-white focus:border-[#132b45] transition-all ${error ? "border-red-400" : "border-gray-100"
          }`}
      />
    </div>
    {error && <span className="text-red-500 text-xs mt-1 font-medium ml-1">⚠ {error}</span>}
  </div>
);

const PreviewItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50/50 border border-gray-100">
    <div className="mt-1">{icon}</div>
    <div>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</p>
      <p className="text-gray-800 font-semibold break-words">{value || "Not provided"}</p>
    </div>
  </div>
);

export default VisaHistoryForm;